<template>
  <div>
    <div class="w-80">
      <NLoading v-if="isLoadingSymbol" class="text-brand-primary" size="10" />
      <div v-else class="flex justify-between items-baseline p-3">
        <div class="flex flex-col">
          <div class="inline-flex items-center">
            <SymbolTradeStateIcon
              class="me-2"
              :trading-state-code="symbol.instrumentTradingState"
            />

            <Retail :isin="getSymbolIsin(symbol)">
              <NText variant="h3" tag="h3" weight="bold">
                {{ symbol.instrumentName }}
              </NText>
            </Retail>
          </div>

          <NText variant="caption1" class="text-neutral-secondary">
            {{ symbol.instrumentTitle }}
          </NText>
        </div>

        <div class="flex flex-col justify-around">
          <div class="inline-flex items-center">
            <NumberFormatter
              #="{ number }"
              :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
              maximum-fraction-digits="2"
              sign-display="always"
              number-style="percent"
            >
              <NText
                weight="bold"
                dir="ltr"
                class="mx-1"
                :class="{
                  'text-brand-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) > 0,
                  'text-critical-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) < 0,
                }"
              >
                ({{ number }})
              </NText>
            </NumberFormatter>

            <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
              <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
                <NText :key="number" variant="h1" tag="h1" weight="bold">
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-2 px-4 bg-neutral-secondary rounded-md">
      <div class="flex justify-between items-center py-3">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderSide') }}
        </NText>

        <NText
          v-if="selectedOrder.orderSideId === ORDER_SIDES.buy"
          variant="caption1"
          weight="medium"
          class="pr-2 text-brand-primary"
        >
          {{ t('common.buy') }}
        </NText>

        <NText
          v-if="selectedOrder.orderSideId === ORDER_SIDES.sell"
          variant="caption1"
          weight="medium"
          class="pr-2 text-critical-primary"
        >
          {{ t('common.sell') }}
        </NText>
      </div>

      <div
        class="flex justify-between items-center pb-3 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_state') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ selectedOrder.orderState }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-3">
        <NText variant="caption1" weight="medium">
          {{ t('misc.quantity') }}
          ({{ t('modules.order.executed') }})
        </NText>

        <div class="flex flex-row items-center">
          <NumberFormatter
            #="{ number }"
            :value="selectedOrder.executedQuantity"
          >
            <NText variant="caption1" weight="medium" class="pl-1">
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="selectedOrder.quantity">
            <NText variant="caption1" weight="medium">{{ number }}</NText>
          </NumberFormatter>
        </div>
      </div>

      <div class="flex justify-between items-center pb-3">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_price') }}
        </NText>

        <div>
          <NumberFormatter #="{ number }" :value="selectedOrder.price">
            <NText
              :key="number"
              variant="caption1"
              weight="medium"
              class="mx-1"
            >
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary">
            {{ t('misc.rial') }}
          </NText>
        </div>
      </div>

      <div
        class="flex justify-between items-center border-b border-neutral-tertiary pb-3"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.orderTotalValue') }}
        </NText>

        <NLoading v-if="isLoadingSymbol" class="text-brand-primary" size="10" />
        <div v-else>
          <NumberFormatter #="{ number }" :value="totalValue" round>
            <NText
              :key="number"
              variant="caption1"
              weight="medium"
              class="mx-1"
            >
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary">
            {{ t('misc.rial') }}
          </NText>
        </div>
      </div>

      <div v-if="isOpenOrder(selectedOrder)">
        <div
          v-if="!isPendingOrder(selectedOrder)"
          class="flex justify-between items-center py-3"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.position') }}
          </NText>

          <NText variant="caption1" weight="medium">
            {{ orderPositionInfo?.position }}
          </NText>
        </div>

        <div class="flex justify-between items-center pb-3">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.waitingـvolume') }}
          </NText>
          <div>
            <NumberFormatter
              #="{ number }"
              :value="orderPositionInfo?.waitingVolume"
              round
            >
              <NText variant="caption2" weight="medium" dir="ltr">
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <div
          class="flex justify-between items-center pb-3 border-b border-neutral-tertiary"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.waiting_trade_value') }}
          </NText>
          <div>
            <NumberFormatter
              #="{ number }"
              :value="orderPositionInfo?.totalWaitingTradeValue"
              round
            >
              <NText variant="caption2" weight="medium" dir="ltr">
                {{ number }}
              </NText>

              <NText variant="overline" class="text-neutral-secondary ms-1">
                {{ t('misc.rial') }}
              </NText>
            </NumberFormatter>
          </div>
        </div>
      </div>

      <div class="flex justify-between items-center py-3">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.creationTime') }}
        </NText>

        <div>
          <NText variant="caption1" weight="medium" class="pr-2">
            {{ creationTime }}
          </NText>
        </div>
      </div>

      <div class="flex justify-between items-center pb-3">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>
        <NText variant="caption1" weight="medium">
          {{ orderValidity[selectedOrder.validityTypeId] }}
        </NText>
      </div>

      <div
        v-if="selectedOrder.errorDescription"
        class="flex justify-between pb-3"
      >
        <NText variant="caption1" weight="medium">
          {{ t('common.description') }}
        </NText>
        <NText variant="caption1" weight="medium" class="mr-4">
          {{ selectedOrder.errorDescription }}
        </NText>
      </div>
    </div>

    <div v-if="isOpenOrder(selectedOrder)" class="flex items-center mt-6 gap-2">
      <NButton
        variant="fill"
        color="gray"
        size="small"
        class="w-full"
        @click="openEditOrderDialog"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.editOrder') }}
        </NText>
      </NButton>

      <NButton
        ref="deleteOrderButtonRef"
        variant="outline"
        color="red"
        class="w-full"
        size="small"
        @click="deleteOrderDialog"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.cancelOrder') }}
        </NText>
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog } from '@negar/core';
import { InitialPublicOfferItem, OrderItem } from '@libs/api-reference';
import {
  getOrderSideValue,
  ORDER_SIDES,
  ORDER_SIDES_TABS,
  useDeleteOrderQuery,
  VALIDITY_TYPES,
  useGetOrderPosition,
  isOpenOrder,
  isPendingOrder,
} from '@core/order';
import {
  calcSymbolBuyValue,
  calcSymbolSellValue,
  getSymbolIsin,
  useSymbolDeductions,
} from '@core/symbol';

import {
  OrderFormDetail,
  OrderFormDialog,
  DeleteOrderDialogFooter,
  ORDER_EXECUTION_TYPE,
  HELIUM_DEVICE_TYPE,
} from '@/modules/order';
import { useIpoDialogs } from '@/modules/ipo';
import {
  Retail,
  SymbolTradeStateIcon,
  useLiveSymbolQuery,
} from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

type OrderDetailProps = {
  selectedOrder: OrderItem;
  offer?: InitialPublicOfferItem;
};

const props = defineProps<OrderDetailProps>();

const { t, d } = useI18n();

const { add: addDialog } = useDialog();

const { openEditOfferDialog, openDeleteOfferDialog } = useIpoDialogs();

const isin = ref(props.selectedOrder?.instrumentIdentification as string);
const deleteOrderButtonRef = ref<HTMLElement>();

const { symbol, isLoadingSymbol } = useLiveSymbolQuery(isin);
const { findWageConfigBySymbol } = useSymbolDeductions();

const { deleteOrder, isLoadingDeleteOrder } = useDeleteOrderQuery({
  orderExecutionType: ORDER_EXECUTION_TYPE,
  deviceType: HELIUM_DEVICE_TYPE,
});
const { orderPositionInfo } = useGetOrderPosition(props.selectedOrder);

const wage = computed(() => findWageConfigBySymbol(unref(symbol)));

const totalValue = computed(() =>
  props.selectedOrder?.orderSideId === ORDER_SIDES.buy
    ? calcSymbolBuyValue(
        {
          quantity: Number(props.selectedOrder?.quantity),
          price: Number(props.selectedOrder?.price),
        },
        unref(wage)
      )
    : calcSymbolSellValue(
        {
          quantity: Number(props.selectedOrder?.quantity),
          price: Number(props.selectedOrder?.price),
        },
        unref(wage)
      )
);

const validityDayTitle = computed(() => {
  const creationDay = new Date(props.selectedOrder.entryDate).getDate();
  const today = new Date().getDate();

  return creationDay === today
    ? t('misc.today')
    : d(props.selectedOrder.entryDate, 'shortDate');
});

const creationTime = computed(
  () => `${d(props.selectedOrder.entryDate, 'shortDate')} ,
    ${d(props.selectedOrder.entryDate, 'shortTime')}`
);

const deleteOrderTitle = computed(
  () =>
    `${t('modules.order.cancelOrder')} ${
      props.selectedOrder?.orderSideId === ORDER_SIDES.buy
        ? t('common.buy')
        : t('common.sell')
    } ${props.selectedOrder?.instrumentName}`
);

const title = computed(() =>
  props.selectedOrder.orderSideId === ORDER_SIDES.buy
    ? `${t('modules.order.edit')} ${t('common.buy')} ${
        symbol.value.instrumentName
      }`
    : `${t('modules.order.edit')} ${t('common.sell')} ${
        symbol.value.instrumentName
      }`
);

const deleteOrderDialog = () => {
  if (props.offer) {
    openDeleteOfferDialog({
      offer: props.offer,
      orderId: props.selectedOrder.orderId,
    });
  } else {
    addDialog({
      bodyClass: '!w-1/5',
      title: deleteOrderTitle.value,
      content: t('modules.order.cancelOrderQuestion'),
      referenceElementRef: deleteOrderButtonRef,
      placement: 'top',
      footer: computed(() => ({
        component: DeleteOrderDialogFooter,
        props: {
          order: props.selectedOrder,
          loadingDeleteOrder: isLoadingDeleteOrder.value,
          onDeleteOrder: deleteOrder,
        },
      })),
    });
  }
};

const openEditOrderDialog = () => {
  if (props.offer) {
    openEditOfferDialog({
      offer: props.offer,
      orderId: props.selectedOrder.orderId,
    });
  } else {
    addDialog({
      title: title.value,
      expandableLabel: t('modules.order.symbol_detail'),
      expandable: true,
      defaultExpanded: true,
      multiple: true,
      content: computed(() => ({
        component: OrderFormDialog,
        props: {
          isin: isin.value,
          symbol: symbol.value,
          baseModel: {
            quantity: props.selectedOrder.quantity,
            price: props.selectedOrder.price,
          },
          type: getOrderSideValue(props.selectedOrder.orderSideId),
          tabs:
            props.selectedOrder.orderSideId === ORDER_SIDES.buy
              ? ORDER_SIDES_TABS.buy
              : ORDER_SIDES_TABS.sell,
          isEditOrderForm: true,
          selectedOrder: props.selectedOrder,
          orderId: props.selectedOrder.orderId,
        },
      })),
      expandedContent: computed(() => ({
        component: OrderFormDetail,
        props: {
          isin: isin.value,
          orderId: props.selectedOrder.orderId,
        },
      })),
    });
  }
};

const orderValidity = computed(() => ({
  [VALIDITY_TYPES.Day]: validityDayTitle.value,
  [VALIDITY_TYPES.GoodTillCancelled]: t('common.cancellation'),
  [VALIDITY_TYPES.GoodTillDate]: props.selectedOrder.validityDate
    ? d(props.selectedOrder.validityDate, 'shortDate')
    : '',
}));
</script>
